import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import '../../styles/CommonStyles.css';
import authService from '../../services/authService';

function ResetPasswordPage() {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Extract token from URL query parameters
    const query = new URLSearchParams(location.search);
    const resetToken = query.get('token');
    
    if (!resetToken) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    } else {
      setToken(resetToken);
    }
  }, [location]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    setError('');
  };
  
  const validateForm = () => {
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Call the auth service to reset the password
      await authService.resetPassword(token, formData.password);
      setSuccess(true);
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/member-login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.response?.data?.error || 'Unable to reset password. The link may have expired or is invalid.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <Header />
      
      <div className="page-header">
        <h1>RESET PASSWORD</h1>
        <p>Create a new password for your account</p>
      </div>
      
      <section className="content-section">
        <div className="login-container" style={{ 
          maxWidth: '500px', 
          margin: '0 auto',
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: 'var(--box-shadow)'
        }}>
          <h2 style={{ 
            textAlign: 'center', 
            marginBottom: '2rem',
            color: 'var(--primary-color)'
          }}>
            Reset Password
          </h2>
          
          {!success ? (
            <>
              <p style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                Please enter your new password below.
              </p>
              
              {error && (
                <div style={{ 
                  padding: '0.75rem 1rem',
                  marginBottom: '1.5rem',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  borderRadius: '4px',
                  border: '1px solid #f5c6cb'
                }}>
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label 
                    htmlFor="password" 
                    style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600' 
                    }}
                  >
                    New Password
                  </label>
                  <input 
                    type="password" 
                    id="password" 
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    style={{ 
                      width: '100%', 
                      padding: '0.8rem', 
                      borderRadius: '4px', 
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <label 
                    htmlFor="confirmPassword" 
                    style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontWeight: '600' 
                    }}
                  >
                    Confirm New Password
                  </label>
                  <input 
                    type="password" 
                    id="confirmPassword" 
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    style={{ 
                      width: '100%', 
                      padding: '0.8rem', 
                      borderRadius: '4px', 
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSubmitting || !token}
                  style={{ 
                    width: '100%',
                    padding: '0.8rem',
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    fontSize: '1rem',
                    opacity: (isSubmitting || !token) ? 0.7 : 1
                  }}
                >
                  {isSubmitting ? 'SUBMITTING...' : 'SET NEW PASSWORD'}
                </button>
              </form>
            </>
          ) : (
            <div style={{ 
              padding: '1rem',
              marginBottom: '1.5rem',
              backgroundColor: '#d4edda',
              color: '#155724',
              borderRadius: '4px',
              border: '1px solid #c3e6cb',
              textAlign: 'center'
            }}>
              <p style={{ marginBottom: '1rem' }}>
                <strong>Password reset successful!</strong>
              </p>
              <p>
                Your password has been reset successfully. You will be redirected to the login page in a few seconds.
              </p>
            </div>
          )}
          
          <div style={{ 
            textAlign: 'center', 
            marginTop: '2rem',
            borderTop: '1px solid #eee',
            paddingTop: '1.5rem'
          }}>
            <Link to="/member-login" style={{ 
              textDecoration: 'none',
              color: 'var(--primary-color)',
              fontWeight: '500'
            }}>
              Back to Login
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default ResetPasswordPage;
